import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Eye, EyeOff, Lock, Mail } from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { resolvePostLoginPath } from '@/utils/authRedirect'
import { getStoredDeviceToken } from '@/api/device'
import { AccountRecoveryModal } from '@/components/auth/AccountRecoveryModal'
import { AccountRecoverySelfServiceModal } from '@/components/auth/AccountRecoverySelfServiceModal'
import { Button } from '@/components/learner/ui/Button'
import { LearnerHandleInput } from '@/components/common/LearnerHandleInput'
import { validateLearnerHandle, buildLearnerEmail } from '@/utils/learnerEmail'
import { SESSION_ENDED_FLAG } from '@/hooks/learner/useAttendanceWatcher'

const LOGIN_HERO_IMAGE = '/auth/login-img.png'
const BRAND_VISUAL_STYLE = {
  backgroundImage:
    'linear-gradient(90deg, #4d44e9 0%, #4d44e9 18%, #4f47e9 30%, #554cea 49%, #5b4feb 61%, #5d51ec 67%, #6257ee 73%, #6558ef 85%, #6a5eef 91%, #6c63f1 100%)',
}

export default function LoginPage() {
  const navigate = useNavigate()
  const { login, isLoading, error, errorCode, clearError } = useAuthStore()

  const [handle, setHandle] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const { t } = useTranslation('learner')
  const [recoveryOpen, setRecoveryOpen] = useState(false)
  const [selfServiceOpen, setSelfServiceOpen] = useState(false)
  const [sessionEnded, setSessionEnded] = useState(false)

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_ENDED_FLAG) === '1') {
        sessionStorage.removeItem(SESSION_ENDED_FLAG)
        setSessionEnded(true)
      }
    } catch {
      // storage 불가 환경에서는 배너 생략 (best-effort)
    }
  }, [])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    clearError()

    // FormData에서 직접 값 읽기 (controlled input 동기화 문제 우회)
    const formData = new FormData(e.currentTarget)
    const handleValue = (formData.get('handle') as string) || handle
    const passwordValue = (formData.get('password') as string) || password

    if (validateLearnerHandle(handleValue) !== 'ok') return

    try {
      await login({
        email: buildLearnerEmail(handleValue),
        password: passwordValue,
        deviceFingerprint: getStoredDeviceToken(),
      })
      const role = useAuthStore.getState().user?.role
      navigate(resolvePostLoginPath(role, 'learner'))
    } catch {
      // 에러는 store에서 처리
    }
  }

  const handleErrorCode = handle ? validateLearnerHandle(handle) : 'ok'
  const handleError =
    handleErrorCode === 'contains_at'
      ? t('auth.login.handleErrorAt')
      : handleErrorCode === 'invalid_char'
        ? t('auth.login.handleErrorInvalidChar')
        : undefined

  const formPanel = (
    <div className="w-full max-w-[440px] animate-lc-rise lg:max-w-[380px]">
      {/* Header */}
      <div className="pb-4 text-center">
        <h2 className="text-pretty text-lc-v3-h2 font-bold text-lc-text-primary">
          {t('auth.login.title')}
        </h2>
        <p className="mt-2 text-lc-v3-body-1 font-medium text-lc-text-secondary">
          {t('auth.login.subtitle')}
        </p>
      </div>

      {/* Form */}
      <div className="mt-6 sm:mt-10">
        {sessionEnded && (
          <div
            role="status"
            className="mb-4 rounded-xl bg-lc-bg-key-light px-4 py-3 text-lc-v3-body-2 font-semibold text-lc-text-primary"
          >
            {t('auth.sessionEnded')}
          </div>
        )}

        <form className="space-y-8" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {error && (
              <div
                role="alert"
                className="flex animate-lc-slide-up items-start gap-3 rounded-xl border border-lc-error-300 bg-lc-error-50 p-4"
              >
                <AlertCircle
                  className="mt-0.5 size-5 shrink-0 text-lc-text-error"
                  aria-hidden
                />
                <span className="text-lc-v3-body-2 font-medium text-lc-text-error">
                  {errorCode
                    ? t(`apiError.${errorCode}`, {
                        ns: 'common',
                        defaultValue: error ?? '',
                      })
                    : error}
                </span>
              </div>
            )}

            <div className="space-y-5">
              <div>
                <label
                  htmlFor="handle"
                  className="mb-2 block text-lc-v3-body-2 font-semibold text-lc-text-secondary"
                >
                  {t('auth.login.handleLabel')}
                </label>
                <LearnerHandleInput
                  variant="lc"
                  id="handle"
                  name="handle"
                  required
                  leftIcon={
                    <Mail
                      className="size-5 shrink-0 text-lc-text-tertiary"
                      aria-hidden
                    />
                  }
                  value={handle}
                  onChange={setHandle}
                  error={handleError}
                  placeholder="abc1234"
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="mb-2 block text-lc-v3-body-2 font-semibold text-lc-text-secondary"
                >
                  {t('auth.login.passwordLabel')}
                </label>
                <div className="flex h-12 items-center gap-3 rounded-xl border border-lc-disabled-border bg-lc-v3-gray-50 px-4 transition-[border-color,box-shadow] focus-within:border-lc-key-500 focus-within:ring-2 focus-within:ring-lc-key-100">
                  <Lock
                    className="size-5 shrink-0 text-lc-text-tertiary"
                    aria-hidden
                  />
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete="current-password"
                    spellCheck={false}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="min-w-0 flex-1 bg-transparent text-lc-v3-body-2 font-medium text-lc-text-primary outline-none placeholder:text-lc-text-disabled"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="inline-flex size-8 items-center justify-center rounded-lg text-lc-text-tertiary hover:bg-lc-key-50 hover:text-lc-text-secondary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
                    aria-label={
                      showPassword
                        ? t('auth.password.hide')
                        : t('auth.password.show')
                    }
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <EyeOff className="size-[18px]" aria-hidden />
                    ) : (
                      <Eye className="size-[18px]" aria-hidden />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Button
              type="submit"
              variant="primary"
              size="L"
              disabled={isLoading || !handle || !password || !!handleError}
              className="w-full"
            >
              {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
            </Button>

            {/* 회원가입은 lms 쪽에서 진행 — lc-learner 로그인 화면에서는 숨김 */}

            {/* 아이디/비밀번호 분실 안내 (Slice G1-B) */}
            <div className="pt-1 text-center">
              <button
                type="button"
                onClick={() => setSelfServiceOpen(true)}
                className="rounded-lg px-2 py-1 text-lc-v3-body-2 font-semibold text-lc-text-link hover:text-lc-text-link-hover hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
              >
                {t('auth.recovery.linkText')}
              </button>
            </div>
          </div>
        </form>
      </div>

      <AccountRecoverySelfServiceModal
        isOpen={selfServiceOpen}
        onClose={() => setSelfServiceOpen(false)}
        onContactAcademy={() => {
          setSelfServiceOpen(false)
          setRecoveryOpen(true)
        }}
      />
      <AccountRecoveryModal
        isOpen={recoveryOpen}
        onClose={() => setRecoveryOpen(false)}
      />
    </div>
  )

  return (
    <div className="min-h-screen bg-lc-bg-primary lg:flex">
      <aside
        className="hidden h-screen w-[560px] shrink-0 py-8 pl-8 lg:flex"
        aria-hidden
      >
        <div
          className="relative min-h-0 flex-1 overflow-hidden rounded-[40px] bg-[#4c57e9]"
          style={BRAND_VISUAL_STYLE}
          data-testid="login-brand-visual"
        >
          <img
            src={LOGIN_HERO_IMAGE}
            alt=""
            className="absolute bottom-0 left-1/2 h-[814px] w-[528px] max-w-none -translate-x-1/2"
          />

          <div className="absolute left-14 top-14 z-10 text-white">
            <span className="inline-flex h-8 items-center rounded-full bg-white/20 px-4 text-[14px] font-bold leading-normal">
              {t('auth.login.hero.tagline')}
            </span>
            <h2 className="mt-4 text-[32px] font-extrabold leading-[1.4] tracking-[-0.5px]">
              {t('auth.login.hero.titleLine1')}
              <br />
              <span className="text-[#ffe72f]">
                {t('auth.login.hero.titleLine2Accent')}
              </span>
              {t('auth.login.hero.titleLine2Rest')}
            </h2>
          </div>
        </div>
      </aside>

      <main className="flex min-h-screen min-w-0 flex-1 items-center justify-center p-6 sm:p-10">
        {formPanel}
      </main>
    </div>
  )
}
